import { createContext, useContext, useEffect, useState, ReactNode, useCallback } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { AuthService, Profile, UserRole, AuthState } from '@/services/authService';
import { useToast } from '@/hooks/use-toast';

interface AuthContextType {
  // Core State
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  roles: UserRole[];
  loading: boolean;
  
  // Computed State
  isAdmin: boolean;
  isEmailVerified: boolean;
  onboardingStatus: 'new' | 'partial' | 'complete';
  
  // Auth Actions
  signUp: (email: string, password: string, metadata?: { first_name?: string; last_name?: string }) => Promise<{ data: any; error: any }>;
  signIn: (email: string, password: string) => Promise<{ data: any; error: any }>;
  signOut: () => Promise<{ error: any }>;
  resetPassword: (email: string) => Promise<{ data: any; error: any }>;
  
  // Profile Actions
  updateProfile: (updates: Partial<Profile>) => Promise<{ profile: Profile | null; error: any }>;
  uploadAvatar: (file: File) => Promise<{ url: string | null; error: any }>;
  refreshProfile: () => Promise<void>;
  
  // Email Actions
  resendEmailVerification: () => Promise<{ data: any; error: any }>;
  markEmailAsVerified: () => Promise<void>;
  
  // Utility Actions
  refreshAuthState: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  // Core State
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [roles, setRoles] = useState<UserRole[]>([]);
  const [loading, setLoading] = useState(true);
  
  // Computed State
  const [isAdmin, setIsAdmin] = useState(false);
  const [isEmailVerified, setIsEmailVerified] = useState(false);
  const [onboardingStatus, setOnboardingStatus] = useState<'new' | 'partial' | 'complete'>('new');
  
  const { toast } = useToast();

  // Fetch complete auth state
  const fetchAuthState = useCallback(async (userId: string) => {
    console.log('fetchAuthState called for userId:', userId);
    try {
      const authState = await AuthService.getFullAuthState(userId);
      console.log('Auth state fetched successfully:', authState);
      
      setProfile(authState.profile);
      setRoles(authState.roles);
      setIsAdmin(authState.isAdmin);
      setIsEmailVerified(authState.isEmailVerified);
      setOnboardingStatus(authState.onboardingStatus);
    } catch (error) {
      console.warn('Failed to fetch auth state:', error);
    }
  }, []);

  // Update computed state when profile changes
  useEffect(() => {
    if (profile) {
      setIsEmailVerified(AuthService.isEmailVerified(profile));
      setOnboardingStatus(AuthService.getOnboardingStatus(profile));
    }
  }, [profile]);

  // Initialize authentication
  useEffect(() => {
    console.log('useAuth useEffect initializing...');
    let mounted = true;

    // Set up auth state listener FIRST (prevents deadlocks)
    const { data: { subscription } } = AuthService.onAuthStateChange(
      (event, session) => {
        if (!mounted) return;

        console.log('Auth state change:', event, session?.user?.id);

        // Synchronous state updates only
        setSession(session);
        setUser(session?.user ?? null);

        if (!session?.user) {
          // No user — clear all auth state and we're done loading
          setProfile(null);
          setRoles([]);
          setIsAdmin(false);
          setIsEmailVerified(false);
          setOnboardingStatus('new');
          setLoading(false);
          return;
        }

        // User exists — keep loading=true until fetchAuthState resolves so
        // route guards (AdminLayout etc.) see the real isAdmin value, not
        // the stale `false` default.  Without this, refreshing an /admin
        // page briefly has {loading:false, user:<you>, isAdmin:false} and
        // the guard kicks you back to "/" before roles load.
        console.log('Deferring fetchAuthState for user:', session.user.id);

        // Defer 50ms so Supabase auth.uid() is populated for RLS, then
        // fetch roles, THEN flip loading to false.
        setTimeout(async () => {
          if (!mounted) return;

          try {
            await fetchAuthState(session.user.id);
          } catch (err) {
            console.warn('Failed to fetch auth state:', err);
          }

          if (mounted) setLoading(false);

          // Log login for SIGNED_IN events (covers email confirmation, OAuth, session restore)
          // This ensures first-time entry after signup is counted as a sign-in
          if (event === 'SIGNED_IN') {
            // Additional delay to ensure the session is fully propagated to Supabase
            setTimeout(async () => {
              if (!mounted) return;
              try {
                const { ActivityLogger } = await import("@/services/activityLogger");
                const success = await ActivityLogger.logLogin(session.user.id, session.user.email || '');
                if (!success) {
                  console.warn('Login activity logging returned false for:', session.user.email);
                }
              } catch (err) {
                console.error('Failed to log login activity:', err);
              }
            }, 100);
          }
        }, 50);
      }
    );

    // Initialize session
    const initializeSession = async () => {
      try {
        const { user: currentUser, session: currentSession } = await AuthService.getCurrentUser();
        
        if (!mounted) return;
        
        setSession(currentSession);
        setUser(currentUser);
        
        if (currentSession?.user?.id) {
          await fetchAuthState(currentSession.user.id);
        } else {
          setProfile(null);
          setRoles([]);
          setIsAdmin(false);
          setIsEmailVerified(false);
          setOnboardingStatus('new');
        }
        
        setLoading(false);
      } catch (error) {
        console.error('Auth initialization error:', error);
        if (mounted) {
          setSession(null);
          setUser(null);
          setProfile(null);
          setRoles([]);
          setIsAdmin(false);
          setIsEmailVerified(false);
          setOnboardingStatus('new');
          setLoading(false);
        }
      }
    };

    initializeSession();
    
    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, [fetchAuthState]);

  // Auth Actions
  const signUp = async (email: string, password: string, metadata?: { first_name?: string; last_name?: string }) => {
    try {
      const result = await AuthService.signUp(email, password, metadata);
      
      if (result.error) {
        toast({
          title: "Sign Up Failed",
          description: result.error.message,
          variant: "destructive"
        });
      } else {
        // Note: Login activity is now logged via the SIGNED_IN event handler
        // This ensures it works whether email confirmation is enabled or not
        toast({
          title: "Account Created!",
          description: result.data?.user?.email_confirmed_at 
            ? "Welcome! You're now signed in."
            : "Please check your email to verify your account.",
        });
      }
      
      return result;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'An unexpected error occurred';
      toast({
        title: "Sign Up Failed",
        description: message,
        variant: "destructive"
      });
      return { data: null, error: { message } };
    }
  };

  const signIn = async (email: string, password: string) => {
    try {
      const result = await AuthService.signIn(email, password);
      
      if (result.error) {
        toast({
          title: "Sign In Failed",
          description: result.error.message,
          variant: "destructive"
        });
      } else if (result.data.user) {
        // Note: Login activity is now logged via the SIGNED_IN event handler
        // This provides a single, consistent logging point for all sign-ins
        toast({
          title: "Welcome back!",
          description: "You have been signed in successfully."
        });
      }
      
      return result;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'An unexpected error occurred';
      toast({
        title: "Sign In Failed",
        description: message,
        variant: "destructive"
      });
      return { data: null, error: { message } };
    }
  };

  const signOut = async () => {
    try {
      // Log logout before signing out
      if (user) {
        const { ActivityLogger } = await import("@/services/activityLogger");
        await ActivityLogger.logLogout(user.id);
      }

      const result = await AuthService.signOut();
      
      // Always clear state regardless of error
      setUser(null);
      setSession(null);
      setProfile(null);
      setRoles([]);
      setIsAdmin(false);
      setIsEmailVerified(false);
      setOnboardingStatus('new');
      
      toast({
        title: "Signed Out",
        description: "You have been signed out successfully."
      });
      
      return result;
    } catch (error) {
      // Still clear state even if sign out failed
      setUser(null);
      setSession(null);
      setProfile(null);
      setRoles([]);
      setIsAdmin(false);
      setIsEmailVerified(false);
      setOnboardingStatus('new');
      
      toast({
        title: "Signed Out",
        description: "You have been signed out."
      });
      
      return { error };
    }
  };

  const resetPassword = async (email: string) => {
    try {
      const result = await AuthService.resetPassword(email);
      
      if (result.error) {
        toast({
          title: "Password Reset Failed",
          description: result.error.message,
          variant: "destructive"
        });
      } else {
        toast({
          title: "Password Reset Sent",
          description: "Check your email for password reset instructions."
        });
      }
      
      return result;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'An unexpected error occurred';
      toast({
        title: "Password Reset Failed",
        description: message,
        variant: "destructive"
      });
      return { data: null, error: { message } };
    }
  };

  // Profile Actions
  const updateProfile = async (updates: Partial<Profile>) => {
    if (!user) return { profile: null, error: new Error('No user logged in') };
    
    try {
      const result = await AuthService.updateProfile(user.id, updates);
      
      if (!result.error && result.profile) {
        setProfile(result.profile);
        toast({
          title: "Profile Updated",
          description: "Your profile has been updated successfully."
        });
      } else {
        toast({
          title: "Update Failed",
          description: result.error?.message || "Failed to update profile",
          variant: "destructive"
        });
      }
      
      return result;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'An unexpected error occurred';
      toast({
        title: "Update Failed",
        description: message,
        variant: "destructive"
      });
      return { profile: null, error: { message } };
    }
  };

  const uploadAvatar = async (file: File) => {
    if (!user) return { url: null, error: new Error('No user logged in') };
    
    try {
      const result = await AuthService.uploadAvatar(user.id, file);
      
      if (!result.error && result.url) {
        // Update profile with new avatar URL
        await updateProfile({ avatar_url: result.url });
        toast({
          title: "Avatar Updated",
          description: "Your avatar has been updated successfully."
        });
      } else {
        toast({
          title: "Upload Failed",
          description: result.error?.message || "Failed to upload avatar",
          variant: "destructive"
        });
      }
      
      return result;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'An unexpected error occurred';
      toast({
        title: "Upload Failed",
        description: message,
        variant: "destructive"
      });
      return { url: null, error: { message } };
    }
  };

  const refreshProfile = async () => {
    if (user?.id) {
      await fetchAuthState(user.id);
    }
  };

  // Email Actions
  const resendEmailVerification = async () => {
    try {
      const result = await AuthService.resendEmailVerification();
      
      if (result.error) {
        toast({
          title: "Verification Failed",
          description: result.error.message,
          variant: "destructive"
        });
      } else {
        toast({
          title: "Verification Sent",
          description: "Please check your email for verification instructions."
        });
      }
      
      return result;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'An unexpected error occurred';
      toast({
        title: "Verification Failed",
        description: message,
        variant: "destructive"
      });
      return { data: null, error: { message } };
    }
  };

  const markEmailAsVerified = async () => {
    if (!user) return;
    
    try {
      await AuthService.markEmailAsVerified(user.id);
      await refreshProfile();
      toast({
        title: "Email Verified",
        description: "Your email has been verified successfully."
      });
    } catch (error) {
      console.error('Failed to mark email as verified:', error);
    }
  };

  // Utility Actions
  const refreshAuthState = async () => {
    if (user?.id) {
      await fetchAuthState(user.id);
    }
  };

  const value = {
    // Core State
    user,
    session,
    profile,
    roles,
    loading,
    
    // Computed State
    isAdmin,
    isEmailVerified,
    onboardingStatus,
    
    // Auth Actions
    signUp,
    signIn,
    signOut,
    resetPassword,
    
    // Profile Actions
    updateProfile,
    uploadAvatar,
    refreshProfile,
    
    // Email Actions
    resendEmailVerification,
    markEmailAsVerified,
    
    // Utility Actions
    refreshAuthState,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}